import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { useEffect } from "react";
import { toast } from "sonner";

interface MoldAlert {
  id: string;
  name: string;
  code: string | null;
  current_uses: number;
  max_uses: number | null;
  maintenance_hours: number;
  usagePercentage: number;
  severity: "critical" | "warning" | "normal";
}

export function useMoldMaintenanceMonitor() {
  const { data: moldsNeedingMaintenance } = useQuery({
    queryKey: ["molds-maintenance-alerts"],
    queryFn: async () => {
      const { data, error } = await api
        .from("molds")
        .select("*")
        .eq("is_active", true);

      if (error) throw error;

      const alerts: MoldAlert[] = [];

      data?.forEach((mold) => {
        if (mold.max_uses && mold.current_uses !== null) {
          const usagePercentage = (mold.current_uses / mold.max_uses) * 100;
          
          if (usagePercentage >= 90) {
            alerts.push({
              ...mold,
              usagePercentage,
              severity: "critical",
            });
          } else if (usagePercentage >= 75) {
            alerts.push({
              ...mold,
              usagePercentage,
              severity: "warning",
            });
          }
        }
      });

      return alerts;
    },
    refetchInterval: 60000, // Refresh every minute
  });

  // Show toast notifications for critical alerts
  useEffect(() => {
    const criticalMolds = moldsNeedingMaintenance?.filter(
      (mold) => mold.severity === "critical"
    );

    if (criticalMolds && criticalMolds.length > 0) {
      criticalMolds.forEach((mold) => {
        toast.error(
          `⚠️ Molde "${mold.name}" requiere mantenimiento urgente (${mold.usagePercentage.toFixed(0)}% de uso)`,
          {
            duration: 10000,
            id: `mold-critical-${mold.id}`,
          }
        );
      });
    }
  }, [moldsNeedingMaintenance]);

  return { moldsNeedingMaintenance };
}
